import React from "react";
import { useNavigate } from "react-router-dom";
import { Gem, ArrowRight } from "lucide-react";

const NavLinks = () => (
  <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-vault-panel/80">
    <a href="#features" className="hover:text-vault-panel transition-colors">Rewards</a>
    <a href="#workflow" className="hover:text-vault-panel transition-colors">Requests</a>
    <a href="#features" className="hover:text-vault-panel transition-colors">About</a>
  </nav>
);

const FeatureCard = ({ tag, title, body }) => (
  <div className="rounded-3xl bg-vault-panel p-7 sm:p-8 shadow-[0_30px_60px_-30px_rgba(0,0,0,0.55)] transition-transform duration-300 hover:-translate-y-1">
    <div className="text-xs font-bold uppercase tracking-widest text-vault-accent mb-8">{tag}</div>
    <h3 className="font-display text-2xl font-extrabold text-vault-cream mb-4">{title}</h3>
    <p className="text-sm leading-relaxed text-vault-subtle">{body}</p>
  </div>
);

export default function Landing() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-vault-bg">
      {/* Header */}
      <header className="max-w-7xl mx-auto px-5 sm:px-8 pt-8 pb-6 flex items-center justify-between">
        <div>
          <div className="font-display text-3xl font-extrabold text-vault-panel leading-none tracking-tight">The Vault</div>
          <div className="text-sm text-vault-panel/60 mt-1">Clark Academy Employee Rewards</div>
        </div>
        <div className="flex items-center gap-6">
          <NavLinks />
          <button
            onClick={() => navigate("/login")}
            className="rounded-2xl bg-vault-accent text-vault-panel font-bold text-sm px-6 py-3 hover:bg-vault-accentdark transition-colors"
          >
            Sign in
          </button>
        </div>
      </header>

      {/* Hero panel */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="relative overflow-hidden rounded-[2rem] bg-vault-panel p-7 sm:p-12 lg:p-16 shadow-[0_50px_90px_-40px_rgba(0,0,0,0.6)]">
          <div className="absolute top-0 left-1/2 -translate-x-1/4 w-[40rem] h-[40rem] rounded-full bg-vault-accent/10 blur-[120px] pointer-events-none" />
          <div className="relative grid lg:grid-cols-[1.4fr_1fr] gap-10 lg:gap-16 items-start">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-vault-accent mb-6 max-w-sm">
                An exclusive rewards collection from Clark Academy
              </div>
              <h1 className="font-display font-900 font-black text-vault-cream text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
                The Vault<br />of Clark<br />Academy
              </h1>
              <p className="text-vault-subtle text-base sm:text-lg leading-relaxed mt-7 max-w-lg">
                Collect, redeem, celebrate. Reward attendance, milestones, monthly consistency, and exceptional moments with a gem-based employee rewards experience.
              </p>
              <div className="flex flex-wrap items-center gap-3 mt-9">
                <button
                  onClick={() => navigate("/register")}
                  className="group inline-flex items-center gap-2 rounded-2xl bg-vault-accent text-vault-panel font-bold px-7 py-3.5 hover:bg-vault-accentdark transition-colors"
                >
                  Create account
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </button>
                <button
                  onClick={() => navigate("/login")}
                  className="rounded-2xl bg-vault-panel3 text-vault-cream font-semibold px-7 py-3.5 hover:bg-vault-panel2 transition-colors"
                >
                  Member login
                </button>
              </div>
            </div>

            {/* Right sub-panel */}
            <div className="rounded-3xl bg-vault-panel2/70 border border-white/5 p-7 sm:p-8">
              <p className="font-display text-2xl sm:text-3xl font-extrabold text-vault-accent leading-snug">
                “Greatness is built in the quiet moments no one sees.”
              </p>
              <ul className="mt-8 space-y-4">
                {[
                  "Automatic birthday and anniversary gems",
                  "Quarterly redemptions with admin approval",
                  "Meeting check-ins with instant reward logic",
                  "Director and admin recognition tools",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-3 text-sm text-vault-cream/90">
                    <Gem className="h-4 w-4 text-vault-accent mt-0.5 shrink-0" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Feature cards */}
      <section id="features" className="max-w-7xl mx-auto px-5 sm:px-8 py-10 lg:py-14">
        <div className="grid md:grid-cols-3 gap-5 lg:gap-6">
          <FeatureCard tag="Curated" title="Editorial luxury design" body="Taupe stock, dark rich panels, gold headlines, magazine-style layouts, and celebration-first messaging throughout the rewards experience." />
          <FeatureCard tag="Automated" title="Milestones and meetings" body="Automatic birthday and anniversary gems, monthly awards, and meeting attendance rewards with role-based point levels." />
          <FeatureCard tag="Controlled" title="Approval workflow" body="HR review and Director final approvals for redemptions and requests, with reporting, history timelines, and role-based control pages." />
        </div>
      </section>

      <footer className="max-w-7xl mx-auto px-5 sm:px-8 py-10 text-sm text-vault-panel/50 border-t border-black/10 mt-4">
        © {new Date().getFullYear()} Clark Academy · The Vault Employee Rewards
      </footer>
    </div>
  );
}
